/**
 * UIController - Manages user interface interactions and updates
 */

import html2canvas from 'html2canvas';

export class UIController {
  constructor() {
    this.mapController = null;
    this.apiService = null;
    this.educationEngine = null;
    this.biomeService = null;
    this.offlineContentManager = null;
    this.userAnalytics = null;
    this.activeTab = 'explore';
    this.panelVisible = true;
    this.notifications = [];
  }

  /**
   * Set map controller reference
   */
  setMapController(mapController) {
    this.mapController = mapController;
  }

  /**
   * Set education engine reference
   */
  setEducationEngine(educationEngine) {
    this.educationEngine = educationEngine;
  }

  /**
   * Set biome service reference
   */
  setBiomeService(biomeService) {
    this.biomeService = biomeService;
  }


  /**
   * Set API service reference
   */
  setAPIService(apiService) {
    this.apiService = apiService;
  }

  /**
   * Set offline content manager reference
   */
  setOfflineContentManager(offlineContentManager) {
    this.offlineContentManager = offlineContentManager;
    if (!offlineContentManager) {
      return;
    }

    offlineContentManager.onDownloadStarted = (packId, pack) => {
      const packName = pack?.name || packId;
      this.showNotification(`Downloading ${packName} for offline use`, 'info');
      this.userAnalytics?.trackInteraction?.('offline_download_start', packId);
    };

    offlineContentManager.onDownloadCompleted = (packId, pack) => {
      const packName = pack?.name || packId;
      this.showNotification(`${packName} ready for offline learning`, 'success');
      this.userAnalytics?.trackInteraction?.('offline_download_complete', packId);
    };

    offlineContentManager.onDownloadError = (packId, _pack, error) => {
      this.showNotification(`Download failed for ${packId}: ${error.message}`, 'error');
      this.userAnalytics?.trackError?.(error, 'offline_download_error');
    };
  }

  /**
   * Set analytics reference
   */
  setUserAnalytics(userAnalytics) {
    this.userAnalytics = userAnalytics;
  }

  /**
   * Initialize UI components and event listeners
   */
  initialize() {
    this.setupTabSystem();
    this.setupControlButtons();
    this.setupPanelToggle();
    this.setupMapEventListeners();
    this.setupKeyboardShortcuts();
    this.setupServiceWorkerListeners();
    this.ensureAnalyticsConsent();

    // Show initial help message
    this.showWelcomeMessage();

    console.log('UI Controller initialized');
  }

  /**
   * Set up tab system for info panel
   */
  setupTabSystem() {
    const tabButtons = document.querySelectorAll('.tab-button');

    tabButtons.forEach(button => {
      button.addEventListener('click', (event) => {
        const tabId = event.currentTarget.dataset.tab;
        this.switchTab(tabId);
      });
    });
  }

  /**
   * Switch to a specific tab
   */
  switchTab(tabId) {
    // Update active tab
    this.activeTab = tabId;

    // Update button states
    document.querySelectorAll('.tab-button').forEach(button => {
      const isActive = button.dataset.tab === tabId;
      button.classList.toggle('active', isActive);
      button.setAttribute('aria-selected', isActive);
    });

    // Update content visibility
    document.querySelectorAll('.tab-content').forEach(content => {
      const isActive = content.dataset.tab === tabId;
      content.classList.toggle('active', isActive);
    });

    // Handle tab-specific initialization
    this.onTabSwitch(tabId);

    this.userAnalytics?.trackInteraction?.('switch_tab', tabId);
  }

  /**
   * Handle tab switch events
   */
  onTabSwitch(tabId) {
    switch (tabId) {
      case 'learn':
        this.updateLearningContent();
        break;
      case 'quiz':
        this.initializeQuizMode();
        break;
      case 'explore':
      default:
        // Default explore mode - no special initialization needed
        break;
    }
  }

  /**
   * Set up control buttons
   */
  setupControlButtons() {
    // Logout button (create if missing)
    let logoutBtn = document.getElementById('btn-logout');
    if (!logoutBtn) {
      const controls = document.getElementById('controls');
      if (controls) {
        logoutBtn = document.createElement('button');
        logoutBtn.id = 'btn-logout';
        logoutBtn.className = 'control-btn';
        logoutBtn.title = 'Log out';
        const icon = document.createElement('span');
        icon.className = 'btn-icon';
        icon.textContent = '?';
        const text = document.createElement('span');
        text.className = 'btn-text';
        text.textContent = 'Logout';
        logoutBtn.appendChild(icon);
        logoutBtn.appendChild(text);
        controls.appendChild(logoutBtn);
      }
    }
    if (logoutBtn) {
      logoutBtn.addEventListener('click', async () => {
        await this.handleLogout(true);
        this.userAnalytics?.trackInteraction?.('logout', 'controls');
      });
    }
    // Reset button
    const resetBtn = document.getElementById('btn-reset');
    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        this.mapController?.resetView();
        this.showNotification('Map view reset', 'info');
        this.userAnalytics?.trackInteraction?.('reset_view', 'map_controls');
      });
    }

    // Screenshot button
    const screenshotBtn = document.getElementById('btn-screenshot');
    if (screenshotBtn) {
      screenshotBtn.addEventListener('click', () => {
        this.takeScreenshot();
        this.userAnalytics?.trackInteraction?.('screenshot', 'map_controls');
      });
    }

    // Accessibility button
    const accessibilityBtn = document.getElementById('btn-accessibility');
    if (accessibilityBtn) {
      accessibilityBtn.addEventListener('click', () => {
        this.toggleHighContrast();
        this.userAnalytics?.trackInteraction?.('toggle_high_contrast', 'map_controls');
      });
    }
  }

  /**
   * Handle logout via API service when available
   */
  async handleLogout(allSessions = false) {
    try {
      if (this.apiService && typeof this.apiService.logout === 'function') {
        await this.apiService.logout(allSessions);
      } else {
        localStorage.removeItem('landgrid_access_token');
        localStorage.removeItem('landgrid_refresh_token');
        localStorage.removeItem('landgrid_user');
      }
      this.showNotification('Logged out successfully', 'success');
      setTimeout(() => window.location.reload(), 800);
    } catch (error) {
      console.error('Logout failed:', error);
      this.showNotification('Logout failed. Please try again.', 'error');
    }
  }


  /**
   * Set up panel toggle functionality
   */
  setupPanelToggle() {
    const panelToggle = document.getElementById('panel-toggle');
    const panelContent = document.getElementById('panel-content');
    const panelIcon = document.querySelector('.panel-toggle-icon');

    if (panelToggle && panelContent && panelIcon) {
      panelToggle.addEventListener('click', () => {
        this.panelVisible = !this.panelVisible;
        panelContent.style.display = this.panelVisible ? 'block' : 'none';
        panelIcon.textContent = this.panelVisible ? '−' : '+';
        panelToggle.setAttribute('aria-expanded', this.panelVisible);
      });
    }
  }

  /**
   * Set up map event listeners
   */
  setupMapEventListeners() {
    // Cell hover events
    document.addEventListener('cell:hover', (event) => {
      this.updateCellInfo(event.detail);
    });

    // Cell selection events
    document.addEventListener('cell:select', (event) => {
      this.updateCellInfo(event.detail, true);
      this.updateLearningContent(event.detail);
    });

    // Cell clear events
    document.addEventListener('cell:clear', () => {
      this.clearCellInfo();
    });
  }

  /**
   * Set up keyboard shortcuts
   */
  setupKeyboardShortcuts() {
    document.addEventListener('keydown', (event) => {
      // Tab switching with number keys
      if (event.key >= '1' && event.key <= '3' && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
        const tabIndex = parseInt(event.key) - 1;
        const tabs = ['explore', 'learn', 'quiz'];
        if (tabs[tabIndex]) {
          this.switchTab(tabs[tabIndex]);
        }
      }

      // Panel toggle with 'P' key
      if (event.key.toLowerCase() === 'p' && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
        document.getElementById('panel-toggle')?.click();
      }
    });
  }

  /**
   * Update cell information display
   */
  updateCellInfo(cellData, isSelected = false) {
    const cellInfoEl = document.getElementById('cell-info');
    const biomeNarrativeEl = document.getElementById('biome-narrative');

    if (cellInfoEl) {
      const action = isSelected ? 'Selected' : 'Viewing';
      const latText = cellData.lat.toFixed(2);
      const lonText = cellData.lon.toFixed(2);
      cellInfoEl.innerHTML = `
        <strong>${action}:</strong> ${latText}&deg;, ${lonText}&deg;<br>
        <strong>Biome:</strong> ${cellData.biome}
      `;
    }

    if (biomeNarrativeEl && cellData.description) {
      biomeNarrativeEl.textContent = cellData.description;
    }

    // Update learning content if in learn tab
    if (this.activeTab === 'learn') {
      this.updateLearningContent(cellData);
    }

    // Update quiz if in quiz tab
    if (this.activeTab === 'quiz') {
      this.updateQuizContent(cellData);
    }
  }

  /**
   * Clear cell information display
   */
  clearCellInfo() {
    const cellInfoEl = document.getElementById('cell-info');
    const biomeNarrativeEl = document.getElementById('biome-narrative');

    if (cellInfoEl) {
      cellInfoEl.textContent = 'Hover or click a grid cell to see details about that region.';
    }

    if (biomeNarrativeEl) {
      biomeNarrativeEl.textContent = 'Biomes represent large ecological areas with similar climate, plants, and animals. Each colored grid cell shows a different biome type.';
    }
  }

  /**
   * Update learning content
   */
  updateLearningContent(cellData = null) {
    const funFactEl = document.getElementById('fun-fact');

    if (funFactEl) {
      if (cellData && this.biomeService) {
        const funFact = this.biomeService.getBiomeFunFact(cellData.biome);
        funFactEl.innerHTML = `
          <strong>${cellData.biome} Biome:</strong><br>
          ${funFact}
        `;
      } else {
        funFactEl.textContent = 'Explore different biomes to discover amazing facts about our planet!';
      }
    }
  }

  /**
   * Initialize quiz mode
   */
  initializeQuizMode() {
    const questionEl = document.getElementById('quiz-question');
    const optionsEl = document.getElementById('quiz-options');
    const feedbackEl = document.getElementById('quiz-feedback');

    if (questionEl) {
      questionEl.textContent = 'Click on a biome to start the quiz!';
    }

    if (optionsEl) {
      optionsEl.innerHTML = '';
    }

    if (feedbackEl) {
      feedbackEl.innerHTML = '';
    }
  }

  /**
   * Update quiz content for selected biome
   */
  updateQuizContent(cellData) {
    if (!cellData || !this.biomeService) return;

    const questions = this.biomeService.getQuizQuestions(cellData.biome);

    if (questions.length === 0) {
      this.showGenericQuiz(cellData.biome);
      return;
    }

    // Select a random question
    const question = questions[Math.floor(Math.random() * questions.length)];
    this.displayQuizQuestion(question, cellData.biome);
  }

  /**
   * Display a quiz question
   */
  displayQuizQuestion(questionData, biome) {
    const questionEl = document.getElementById('quiz-question');
    const optionsEl = document.getElementById('quiz-options');
    const feedbackEl = document.getElementById('quiz-feedback');

    if (questionEl) {
      questionEl.innerHTML = `<strong>${biome} Quiz:</strong><br>${questionData.question}`;
    }

    if (optionsEl) {
      optionsEl.innerHTML = '';

      questionData.options.forEach((option, index) => {
        const button = document.createElement('button');
        button.className = 'quiz-option';
        button.textContent = option;
        button.addEventListener('click', () => {
          this.handleQuizAnswer(index, questionData, button, biome);
        });
        optionsEl.appendChild(button);
      });
    }

    if (feedbackEl) {
      feedbackEl.innerHTML = '';
    }
  }

  /**
   * Handle quiz answer selection
   */
  handleQuizAnswer(selectedIndex, questionData, _buttonEl, biome) {
    const optionsEl = document.getElementById('quiz-options');
    const feedbackEl = document.getElementById('quiz-feedback');
    const isCorrect = selectedIndex === questionData.correct;

    this.userAnalytics?.trackInteraction?.('quiz_answer', questionData.id, {
      biome,
      correct: isCorrect
    });

    // Disable all option buttons
    optionsEl.querySelectorAll('.quiz-option').forEach((btn, index) => {
      btn.disabled = true;
      if (index === questionData.correct) {
        btn.classList.add('correct');
      } else if (index === selectedIndex && !isCorrect) {
        btn.classList.add('incorrect');
      }
    });

    // Show feedback
    if (feedbackEl) {
      feedbackEl.className = `quiz-feedback ${isCorrect ? 'correct' : 'incorrect'}`;
      feedbackEl.innerHTML = `
        <strong>${isCorrect ? 'Correct!' : 'Not quite right.'}</strong><br>
        ${questionData.explanation}
      `;
    }

    // Show notification
    this.showNotification(
      isCorrect ? 'Great job!' : 'Keep learning!',
      isCorrect ? 'success' : 'info'
    );
  }

  /**
   * Show generic quiz for biomes without specific questions
   */
  showGenericQuiz(biome) {
    const questionEl = document.getElementById('quiz-question');
    const optionsEl = document.getElementById('quiz-options');
    const feedbackEl = document.getElementById('quiz-feedback');

    if (questionEl) {
      questionEl.innerHTML = `<strong>${biome} Exploration:</strong><br>What characteristic best describes this biome?`;
    }

    if (optionsEl) {
      optionsEl.innerHTML = '<p>Quiz questions for this biome are coming soon! Keep exploring to learn more.</p>';
    }

    if (feedbackEl) {
      feedbackEl.innerHTML = '';
    }
  }

  /**
   * Take a screenshot of the map
   */
  async takeScreenshot() {
    try {
      this.showNotification('Preparing screenshot...', 'info');

      const mapElement = document.getElementById('map');
      if (!mapElement) {
        throw new Error('Map element not found');
      }

      // Hide UI elements temporarily
      const uiElements = document.querySelectorAll('.info-panel, .controls');
      uiElements.forEach(el => el.style.visibility = 'hidden');

      // Capture screenshot
      const canvas = await html2canvas(mapElement, {
        useCORS: true,
        backgroundColor: '#f0f2f5',
        scale: 1
      });

      // Restore UI elements
      uiElements.forEach(el => el.style.visibility = 'visible');

      // Download image
      const link = document.createElement('a');
      link.download = `landgrid-${new Date().toISOString().slice(0, 10)}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();

      this.showNotification('Screenshot saved!', 'success');
      this.userAnalytics?.trackInteraction?.('screenshot_saved', 'map_controls');
    } catch (error) {
      console.error('Screenshot failed:', error);
      this.showNotification('Failed to take screenshot', 'error');
      this.userAnalytics?.trackError?.(error, 'screenshot_failure');
    }
  }

  /**
   * Toggle high contrast mode
   */
  toggleHighContrast() {
    document.body.classList.toggle('high-contrast');
    const isHighContrast = document.body.classList.contains('high-contrast');

    // Save preference
    localStorage.setItem('landgrid-high-contrast', isHighContrast);

    this.showNotification(
      `High contrast ${isHighContrast ? 'enabled' : 'disabled'}`,
      'info'
    );

    this.userAnalytics?.trackInteraction?.('toggle_high_contrast', 'preference', {
      enabled: isHighContrast
    });
  }

  /**
   * Ensure analytics consent has been captured
   */
  ensureAnalyticsConsent() {
    if (!this.userAnalytics?.checkTrackingConsent) return;

    const consent = this.userAnalytics.checkTrackingConsent();
    if (consent !== null) {
      // Reapply stored preference so internal state matches localStorage
      this.userAnalytics.setTrackingConsent(consent);
      return;
    }

    if (document.getElementById('analytics-consent-banner')) {
      return;
    }

    const banner = document.createElement('div');
    banner.id = 'analytics-consent-banner';
    banner.style.cssText = `
      position: fixed;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 11000;
      background: #111827;
      color: white;
      padding: 16px 24px;
      border-radius: 12px;
      box-shadow: 0 10px 30px rgba(15, 23, 42, 0.35);
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      align-items: center;
      max-width: 480px;
      width: calc(100% - 40px);
    `;

    const message = document.createElement('span');
    message.textContent = 'We use optional analytics to improve LandGrid.AI. Allow anonymous usage data?';

    const buttonGroup = document.createElement('div');
    buttonGroup.style.cssText = 'display: flex; gap: 8px; margin-left: auto;';

    const acceptBtn = document.createElement('button');
    acceptBtn.textContent = 'Allow';
    acceptBtn.style.cssText = `
      background: #10b981;
      color: white;
      border: none;
      border-radius: 6px;
      padding: 8px 14px;
      cursor: pointer;
      font-weight: 600;
    `;

    const declineBtn = document.createElement('button');
    declineBtn.textContent = 'No thanks';
    declineBtn.style.cssText = `
      background: transparent;
      color: #f9fafb;
      border: 1px solid rgba(249, 250, 251, 0.6);
      border-radius: 6px;
      padding: 8px 14px;
      cursor: pointer;
      font-weight: 600;
    `;

    acceptBtn.addEventListener('click', () => {
      this.userAnalytics.setTrackingConsent(true);
      this.userAnalytics.flushPendingData?.();
      this.userAnalytics.trackInteraction?.('analytics_consent', 'banner', { accepted: true });
      banner.remove();
      this.showNotification('Analytics enabled. Thank you!', 'success', 4000);
    });

    declineBtn.addEventListener('click', () => {
      this.userAnalytics.setTrackingConsent(false);
      this.userAnalytics.trackInteraction?.('analytics_consent', 'banner', { accepted: false });
      banner.remove();
      this.showNotification('Analytics disabled. You can enable it later from settings.', 'info', 4000);
    });

    buttonGroup.appendChild(acceptBtn);
    buttonGroup.appendChild(declineBtn);
    banner.appendChild(message);
    banner.appendChild(buttonGroup);
    document.body.appendChild(banner);
  }

  /**
   * Listen for service worker messages (sync notifications)
   */
  setupServiceWorkerListeners() {
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker?.addEventListener) {
      return;
    }

    navigator.serviceWorker.addEventListener('message', (event) => {
      const { data } = event;
      if (!data || typeof data !== 'object') return;

      if (data.type === 'SW_SYNC_REQUESTED') {
        this.showNotification('Syncing offline progress…', 'info', 4000);
        this.userAnalytics?.trackInteraction?.('sw_sync_requested', 'service_worker');
      }
    });
  }

  /**
   * Show notification message
   */
  showNotification(message, type = 'info', duration = 3000) {
    const notification = document.createElement('div');
    notification.className = `notification notification-${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: ${type === 'success' ? '#22c55e' : type === 'error' ? '#ef4444' : '#3b82f6'};
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 10000;
      font-size: 14px;
      transition: all 0.3s ease;
      opacity: 0;
    `;

    document.body.appendChild(notification);

    // Animate in
    setTimeout(() => {
      notification.style.opacity = '1';
    }, 10);

    // Remove after duration
    setTimeout(() => {
      notification.style.opacity = '0';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, duration);

    this.notifications.push({ message, type, timestamp: Date.now() });

    // Keep only last 10 notifications
    if (this.notifications.length > 10) {
      this.notifications = this.notifications.slice(-10);
    }
  }

  /**
   * Show welcome message
   */
  showWelcomeMessage() {
    setTimeout(() => {
      this.showNotification('Welcome to LandGrid.AI! Click on any grid cell to explore biomes.', 'info', 5000);
    }, 1000);
  }

  /**
   * Show help information
   */
  showHelp() {
    const helpContent = `
      <div style="max-width: 400px; text-align: left;">
        <h3>How to Use LandGrid.AI</h3>
        <ul style="margin: 0; padding-left: 20px;">
          <li>Click or hover over grid cells to explore biomes</li>
          <li>Use the tabs to switch between Explore, Learn, and Quiz modes</li>
          <li>Press 'R' to reset the map view</li>
          <li>Press 'S' to take a screenshot</li>
          <li>Press 'A' to toggle high contrast mode</li>
          <li>Press 'P' to toggle the info panel</li>
          <li>Use number keys 1-3 to switch tabs</li>
        </ul>
      </div>
    `;

    // Create help modal
    const modal = document.createElement('div');
    modal.className = 'help-modal';
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
    `;

    const content = document.createElement('div');
    content.style.cssText = `
      background: white;
      border-radius: 12px;
      padding: 24px;
      max-width: 90vw;
      max-height: 90vh;
      overflow-y: auto;
    `;

    content.innerHTML = `${helpContent  }<button onclick="this.closest('.help-modal').remove()" style="margin-top: 16px; padding: 8px 16px; border: none; background: #3b82f6; color: white; border-radius: 6px; cursor: pointer;">Close</button>`;

    modal.appendChild(content);
    document.body.appendChild(modal);

    // Close on outside click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.remove();
      }
    });
  }

  /**
   * Load accessibility preferences
   */
  loadAccessibilityPreferences() {
    const highContrast = localStorage.getItem('landgrid-high-contrast') === 'true';
    if (highContrast) {
      document.body.classList.add('high-contrast');
    }
  }

  /**
   * Get current UI state
   */
  getState() {
    return {
      activeTab: this.activeTab,
      panelVisible: this.panelVisible,
      notifications: this.notifications,
      highContrast: document.body.classList.contains('high-contrast')
    };
  }
}



